import type { DataTableColumnFilter } from "@frontend/components/organisms/DataTable";
import { request } from "@frontend/lib/http";

import type { ClientValues } from "../schemas/client";
import type { ConditionsValues } from "../schemas/conditions";
import type {
  Client,
  ClientContact,
  ClientListColumn,
  ClientListDirection,
  ClientListFilter,
  ClientPage,
  ClientQuickview,
  ClientRow,
  ClientTypeOption,
  CommercialCondition,
  Contact,
  PaymentTypeOption,
  ProvenanceOption,
} from "../types";
import { toClientInput, toConditionsInput } from "../utils/payload";

type RequestOptions = {
  signal?: AbortSignal;
};

export type ClientListQuery = {
  search?: string;
  filter?: ClientListFilter;
  page?: number;
  perPage?: number;
  sort?: ClientListColumn | null;
  direction?: ClientListDirection;
  criteria?: DataTableColumnFilter[];
};

export type ClientApi = {
  listClients(
    query: ClientListQuery,
    options?: RequestOptions,
  ): Promise<ClientPage>;
  fetchClient(clientId: number, options?: RequestOptions): Promise<Client>;
  fetchContact(contactId: number, options?: RequestOptions): Promise<Contact>;
  fetchQuickview(
    clientId: number,
    options?: RequestOptions,
  ): Promise<ClientQuickview>;
  fetchClientTypes(options?: RequestOptions): Promise<ClientTypeOption[]>;
  fetchProvenances(options?: RequestOptions): Promise<ProvenanceOption[]>;
  createClient(input: ClientValues, options?: RequestOptions): Promise<Client>;
  updateClient(
    clientId: number,
    input: ClientValues,
    options?: RequestOptions,
  ): Promise<Client>;
  archiveClient(clientId: number, options?: RequestOptions): Promise<ClientRow>;
  deleteClient(clientId: number, options?: RequestOptions): Promise<void>;
  fetchConditions(
    clientId: number,
    options?: RequestOptions,
  ): Promise<CommercialCondition>;
  saveConditions(
    clientId: number,
    input: ConditionsValues,
    options?: RequestOptions,
  ): Promise<CommercialCondition>;
  fetchPaymentTypes(options?: RequestOptions): Promise<PaymentTypeOption[]>;
  fetchPriceTypes(options?: RequestOptions): Promise<PriceTypeRow[]>;
  createClientContact(
    clientId: number,
    input: { contactId: number; typeId: number | null },
    options?: RequestOptions,
  ): Promise<ClientContact>;
};

export const clientApi: ClientApi = {
  listClients(
    { search, filter, page, perPage, sort, direction, criteria },
    options,
  ) {
    return request({
      route: "api_clients_list",
      query: {
        search: search || undefined,
        filter,
        page,
        perPage,
        sort: sort ?? undefined,
        direction: sort ? direction : undefined,
        filters: encodeCriteria(criteria),
      },
      ...options,
    });
  },

  fetchClient(clientId, options) {
    return request({
      route: "api_clients_show",
      params: { clientId },
      ...options,
    });
  },

  fetchContact(contactId, options) {
    return request({
      route: "api_contacts_show",
      params: { contactId },
      ...options,
    });
  },

  fetchQuickview(clientId, options) {
    return request({
      route: "api_clients_quickview",
      params: { clientId },
      ...options,
    });
  },

  fetchClientTypes(options) {
    return request({ route: "api_client_types", ...options });
  },

  fetchProvenances(options) {
    return request({ route: "api_client_provenances", ...options });
  },

  createClient(input, options) {
    return request({
      route: "api_clients_create",
      method: "POST",
      body: { clientInput: toClientInput(input) },
      ...options,
    });
  },

  updateClient(clientId, input, options) {
    return request({
      route: "api_clients_update",
      params: { clientId },
      method: "PUT",
      body: { clientInput: toClientInput(input) },
      ...options,
    });
  },

  archiveClient(clientId, options) {
    return request({
      route: "api_clients_archive",
      params: { clientId },
      method: "POST",
      ...options,
    });
  },

  deleteClient(clientId, options) {
    return request({
      route: "api_clients_delete",
      params: { clientId },
      method: "DELETE",
      ...options,
    });
  },

  fetchConditions(clientId, options) {
    return request({
      route: "api_clients_commercial_conditions_show",
      params: { clientId },
      ...options,
    });
  },

  saveConditions(clientId, input, options) {
    return request({
      route: "api_clients_commercial_conditions_update",
      params: { clientId },
      method: "PUT",
      body: { commercialConditionInput: toConditionsInput(input) },
      ...options,
    });
  },

  fetchPaymentTypes(options) {
    return request({ route: "api_client_payment_types", ...options });
  },

  fetchPriceTypes(options) {
    return request({ route: "api_client_price_types", ...options });
  },

  createClientContact(clientId, input, options) {
    return request({
      route: "api_clients_contacts_add",
      params: { clientId },
      method: "POST",
      body: { clientContactInput: input },
      ...options,
    });
  },
};

function encodeCriteria(
  criteria: DataTableColumnFilter[] | undefined,
): string | undefined {
  if (undefined === criteria || 0 === criteria.length) {
    return undefined;
  }

  return JSON.stringify(
    criteria.map(({ id, operator, value, value2, values }) => ({
      column: id,
      operator,
      value,
      value2,
      values,
    })),
  );
}

export type PriceTypeRow = {
  id: number;
  label: string;
  unitLabel: string | null;
  priceUnitLabel: string | null;
};
